import { useCallback } from "react";
import { useWidgetInstance } from "@salestrak/ui";

import { removePersistedState, usePersistedState } from "#app/lib/hooks/use-persisted-state";

function bandFoldsKey(instanceId: string): string {
  return `widget-band-folds:${instanceId}`;
}

/** Forgets a deleted widget's folds, so its record does not stay behind. */
export function forgetBandFolds(instanceId: string): void {
  removePersistedState(bandFoldsKey(instanceId));
}

/** Band key → folded. `true` is a fold, `false` an explicit open over a band
 *  that folds by default; an absent key leaves the band on its default. */
type BandFolds = Record<string, boolean>;

/**
 * Which bands of a widget's list stand folded — remembered per placed widget
 * (`useWidgetInstance`), per browser, so a reader who put Stale away finds it
 * still away after a reload (Spencer, 2026-10-05: a fold that vanished on
 * reload read as lost work). The store is the viewer's own: `localStorage`,
 * never the widget's workspace-shared `config` — a fold is how one person
 * reads the card, not what the card is. The card and its maximized view
 * share the one record, since both mount the same instance.
 *
 * A band's DEFAULT stays the widget's call (`defaultFolded`): the record only
 * holds the bands the reader has touched, so a widget whose default depends
 * on its data (On track folds while something is owed) keeps deciding the
 * untouched ones. Outside any placement (a gallery preview) the bands share
 * one unplaced record — a preview's folds have no instance to belong to.
 */
export function useBandFolds(): {
  /** Whether the band stands folded: the reader's choice if they made one,
   *  else `defaultFolded`. */
  folded: (key: string, defaultFolded?: boolean) => boolean;
  toggle: (key: string, defaultFolded?: boolean) => void;
} {
  const instance = useWidgetInstance();
  const { state, update } = usePersistedState<BandFolds>(
    bandFoldsKey(instance?.instanceId ?? "unplaced"),
    {},
  );
  const folded = useCallback(
    (key: string, defaultFolded = false) => state[key] ?? defaultFolded,
    [state],
  );
  const toggle = useCallback(
    (key: string, defaultFolded = false) => {
      update((current) => ({ [key]: !(current[key] ?? defaultFolded) }));
    },
    [update],
  );
  return { folded, toggle };
}
